import { useDispatch } from 'react-redux'
import { GridColDef, GridSelectionModel } from '@material-ui/data-grid'
import Options from '@mui/icons-material/MoreVert'
import { IconButton } from '@mui/material'
import { IInvoice, IInvoiceDraft } from 'models/invoices.models'
import {
  getFormattedCurrency,
  getFormattedDate,
  getFormattedTime,
} from 'lib/helpers/shared.helpers'
import { selectInvoicesHeaders } from 'lib/helpers/selectors'
import {
  getFormattedEmisor,
  getFormattedReceptor,
  getFormattedSerieFolio,
  unavailableColumnsForInvoiceDrafts,
} from 'lib/utils/Invoices/invoices.utils'
import { useAppSelector } from 'store/hooks'
import { setSelectedInvoice as setSelectedInvoiceRedux } from 'actions/invoices'
import {
  ICFDIsContainerProps,
  useCFDIsContainerContext,
} from 'containers/Invoices/CFDIs/CFDIsContainer/CFDIsContainer'
import SydTable from 'components/Shared/SydTable/SydTable'
import CFDIArticulo69B from 'components/Invoices/CFDIArticulo69B/CFDIArticulo69B'
import CFDIMovement from 'components/Invoices/CFDIMovement/CFDIMovement'
import CFDIPaymentStatus from 'components/Invoices/CFDIPaymentStatus/CFDIPaymentStatus'
import CFDIStatus from 'components/Invoices/CFDIStatus/CFDIStatus'
import './styles.scss'

const getInvoiceRowGridId = (row: IInvoice | IInvoiceDraft): string | number => {
  const r = row as { id?: string | number; idCfdi?: number; uuid?: string }
  if (r.id !== undefined && r.id !== null) return r.id
  return r.idCfdi ?? r.uuid ?? ''
}

const invoiceRowMatchesSelectionId = (
  row: IInvoice | IInvoiceDraft,
  selectionId: string | number
): boolean => {
  const gridId = getInvoiceRowGridId(row)
  if (gridId === '') return false
  return String(gridId) === String(selectionId)
}

type TCFDIsTableProps = Pick<ICFDIsContainerProps, 'emisor'>

const CFDIsTable = ({ emisor }: TCFDIsTableProps) => {
  const {
    CFDIsPaginatedData,
    isCFDIsTab,
    isInvoiceDraftsTab,
    selectedInvoices,
    isExpandingSelectionToAllFiltered,
    updateState,
    expandSelectionToAllFilteredCFDIs,
  } = useCFDIsContainerContext()

  const dispatch = useDispatch()

  const invoiceHeaders = useAppSelector(selectInvoicesHeaders)

  const { rows } = CFDIsPaginatedData

  const typedRows = rows as IInvoice[]

  const invoicesTableColumns: GridColDef[] = [
    {
      field: 'createdAt',
      headerName: 'Fecha',
      minWidth: 110,
      flex: 1,
      sortable: false,
      renderCell: ({ row: { createdAt } }) => {
        return !isCFDIsTab ? (
          <div className="draft-chip">Prefactura</div>
        ) : (
          getFormattedDate(createdAt)
        )
      },
    },
    {
      field: 'hour',
      headerName: 'Hora',
      minWidth: 110,
      flex: 1,
      sortable: false,
      valueFormatter: ({ row: { hour } }) => getFormattedTime(hour, true),
    },

    {
      field: 'serie',
      headerName: 'Serie-Folio',
      minWidth: 110,
      flex: 1,
      sortable: false,
      valueFormatter: ({ row }) =>
        getFormattedSerieFolio(row.serie, row as IInvoice | IInvoiceDraft),
    },
    {
      field: 'uuid',
      headerName: 'UUID',
      minWidth: 200,
      flex: 2.5,
      sortable: false,
      renderCell: ({ row: { uuid } }) => (
        <div title={uuid.toUpperCase()} className="ellipsis">
          {uuid.toUpperCase()}
        </div>
      ),
    },
    {
      field: 'clients',
      headerName: 'Receptor',
      minWidth: 160,
      flex: 1.5,
      sortable: false,
      renderCell: ({ row: { clients } }) => (
        <div title={getFormattedReceptor(clients)} className="ellipsis">
          {getFormattedReceptor(clients)}
        </div>
      ),
    },
    {
      field: 'emisor',
      headerName: 'Emisor',
      minWidth: 10,
      flex: 1.5,
      sortable: false,
      renderCell: ({ row: { emisor } }) => (
        <div title={getFormattedEmisor(emisor)} className="ellipsis">
          {getFormattedEmisor(emisor)}
        </div>
      ),
    },
    {
      field: 'subtotal',
      headerName: 'Subtotal',
      minWidth: 100,
      flex: 1,
      sortable: false,
      valueFormatter: ({ row: { subtotal } }) => getFormattedCurrency(subtotal),
    },
    {
      field: 'taxes',
      headerName: 'Impuestos',
      minWidth: 120,
      flex: 1,
      sortable: false,
      valueFormatter: ({ row: { taxes } }) => getFormattedCurrency(taxes),
    },
    {
      field: 'total',
      headerName: 'Total',
      width: 70,
      sortable: false,
      valueFormatter: ({ row: { total } }) => getFormattedCurrency(total),
    },
    {
      field: 'movementType',
      headerName: 'Tipo',
      width: 65,
      sortable: false,
      renderCell: ({ row }) => (
        <CFDIMovement type={row.movementType} addTooltip />
      ),
    },
    {
      field: 'status',
      headerName: 'Estatus',
      minWidth: 85,
      flex: 1,
      sortable: false,
      renderCell: ({ row }) => (
        <CFDIStatus status={row.status} row={row} addTooltip />
      ),
    },
    {
      field: 'art69B',
      headerName: 'Art. 69B',
      minWidth: 85,
      flex: 1,
      sortable: false,
      renderCell: ({ row: { clients } }) => (
        <CFDIArticulo69B receptor={clients} addTooltip />
      ),
    },
    {
      field: 'statusPayment',
      headerName: 'Pago',
      minWidth: 75,
      flex: 1,
      sortable: false,
      renderCell: ({ row }) => (
        <CFDIPaymentStatus status={row.statusPayment} row={row} addTooltip />
      ),
    },
    {
      field: 'sent',
      headerName: 'Enviado',
      minWidth: 110,
      flex: 1,
      sortable: false,
      valueFormatter: ({ row: { sent } }) => getFormattedDate(sent),
    },
  ]

  const relevantColumns = isInvoiceDraftsTab
    ? invoicesTableColumns.filter(
        ({ field }) => !unavailableColumnsForInvoiceDrafts.includes(field)
      )
    : invoicesTableColumns

  const finalColumns: GridColDef[] = [
    ...relevantColumns.filter(({ field }) => invoiceHeaders[field]),
    {
      field: '',
      headerName: '',
      width: 50,
      align: 'right',
      sortable: false,
      renderCell: ({ row }) => (
        <IconButton
          size="small"
          color="primary"
          onClick={e => {
            e.stopPropagation()
            updateState(draft => {
              draft.seeMoreAnchor = e.currentTarget
              draft.activeInvoice = row as IInvoice
            })
            dispatch(setSelectedInvoiceRedux(row as IInvoice))
          }}>
          <Options />
        </IconButton>
      ),
    },
  ]

  const handleSelectionModelChange = (data: GridSelectionModel) => {
    const mappedSelection = data
      .map(selectionId =>
        typedRows.find(row =>
          invoiceRowMatchesSelectionId(row, selectionId as string | number)
        )
      )
      .filter((item): item is IInvoice => !!item)

    const allVisibleRowsSelected =
      isCFDIsTab &&
      typedRows.length > 0 &&
      data.length === typedRows.length &&
      typedRows.every(row =>
        data.some(sid =>
          invoiceRowMatchesSelectionId(row, sid as string | number)
        )
      )

    const hasMorePagesThanVisible =
      CFDIsPaginatedData.rowCount > typedRows.length

    if (allVisibleRowsSelected && hasMorePagesThanVisible) {
      void expandSelectionToAllFilteredCFDIs()
      return
    }

    updateState(draft => {
      draft.selectedInvoices = mappedSelection
    })
  }

  return (
    <SydTable
      className="cfdis-table"
      {...CFDIsPaginatedData}
      isLoading={
        CFDIsPaginatedData.isLoading || isExpandingSelectionToAllFiltered
      }
      columns={finalColumns}
      checkboxSelection={isCFDIsTab}
      selectionModel={selectedInvoices.map(inv => getInvoiceRowGridId(inv))}
      onSelectionModelChange={handleSelectionModelChange}
      rowHeight={40}
      noBorder
      noDataText={
        isCFDIsTab
          ? 'No hay CFDIs disponibles'
          : 'No hay Prefacturas disponibles'
      }
    />
  )
}

export default CFDIsTable
